import { useCallback, useEffect, useState } from 'react';
import { accountDelete, accountDeletionPlan, type AccountDeletionPlan } from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { useReadOnlyReason } from '../ui/ReadOnly';
import { sx } from '../ui/designStyles';

/**
 * Delete my account (Console V4, Spec V4 step 2; design T274-317, delVals L37039-37216):
 * one dialog, six versions from the signed-in account, every word of it from the server
 * (GET /account/deletion: the title, lead, what goes, what stays and why, and a Facility
 * Admin's last-active-Admin rule):
 *   Family, Aithora Home      deleted at once
 *   Care Staff, Facility Admin the login goes at once, their name stays on what they wrote
 *   Resident                  a request to the facility
 *   Operator, Sales, Dev      a request to the owner
 *   The owner (Andrew)        cannot be deleted; no input
 * Type DELETE (any case) to enable the button. The public page and the support address
 * stay in the footer for anyone without the app.
 */

export function useDeleteAccountDialog() {
  const [isOpen, setOpen] = useState(false);
  return { isOpen, open: useCallback(() => setOpen(true), []), close: useCallback(() => setOpen(false), []) };
}

const SUPPORT = 'support@aithoracare.com';
const PUBLIC_PAGE = 'app.aithoracare.com/delete-account';

export function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const { client, session, signOut } = useSession();
  const { realRole } = useConsoleRole();
  const roReason = useReadOnlyReason();
  const plan = useQuery<AccountDeletionPlan>((signal) => accountDeletionPlan(client, { signal }), [client]);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ deleted: boolean } | null>(null);
  const big = realRole === 'RESIDENT' || realRole === 'MEMBER';

  const close = useCallback(() => {
    onClose();
    // The account is gone on the server: end the session here too.
    if (done?.deleted) void signOut().catch(() => undefined);
  }, [onClose, done, signOut]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const p = plan.data;
  const d = p?.dialog;
  const owner = p?.kind === 'owner';
  const isReq = p?.mode === 'request_facility' || p?.mode === 'request_owner';
  const sent = !!done || !!p?.pendingRequest;
  const ok = typed.trim().toUpperCase() === 'DELETE';
  const canAct = !!p && p.mode !== 'refused' && !sent;
  const facility = session?.user.orgName ?? 'Your facility';
  const fs = big ? 19 : 14;

  const go = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!ok || busy || roReason) return;
    setBusy(true);
    setError(null);
    try {
      const r = await accountDelete(client, { body: { confirm: 'DELETE' } });
      setDone({ deleted: r.deleted });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nothing was deleted. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const panel = 'position:fixed;z-index:301;left:50%;top:50%;transform:translate(-50%,-50%);width:min(' + (big ? 720 : 640) + 'px,calc(100vw - 32px));max-height:calc(100vh - 40px);overflow-y:auto;overflow-x:hidden;box-sizing:border-box;background:#FCFAF4;border-radius:14px;box-shadow:0 24px 60px rgba(34,39,46,.24);padding:' + (big ? 28 : 22) + 'px;font-size:' + fs + 'px;color:#22272E;';
  const closeStyle = 'flex:0 0 auto;display:inline-flex;align-items:center;min-height:' + (big ? 60 : 32) + 'px;padding:0 ' + (big ? 18 : 10) + 'px;border-radius:8px;font-weight:600;text-decoration:none;color:#3C454F;border:1px solid rgba(34,39,46,.18);';
  const inputStyle = 'flex:1 1 180px;min-width:0;box-sizing:border-box;height:' + (big ? 60 : 40) + 'px;padding:0 14px;border:1px solid rgba(34,39,46,.24);border-radius:9px;background:#fff;font:inherit;font-weight:600;letter-spacing:.06em;color:#22272E;';
  const goStyle = 'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;min-height:' + (big ? 60 : 40) + 'px;padding:0 ' + (big ? 24 : 18) + 'px;border-radius:9px;font-weight:700;text-decoration:none;'
    + (ok && !roReason ? 'background:#B3261E;color:#fff;border:1px solid #B3261E;cursor:pointer;' : 'background:rgba(34,39,46,.06);color:#54606C;border:1px solid rgba(34,39,46,.14);cursor:not-allowed;');
  const doneBody = p?.kind === 'resident'
    ? facility + ' will tell you their answer. It is recorded on the Truth Ledger.'
    : p?.kind === 'team'
      ? 'Andrew will carry it out. It is recorded on the Truth Ledger.'
      : 'Recorded on the audit trail and the Truth Ledger. You are signed out now.';

  return (
    <>
      <a href="#" onClick={(e) => { e.preventDefault(); close(); }} aria-label="Close" style={sx('position:fixed;inset:0;z-index:300;background:rgba(34,39,46,.38);')}></a>
      <div role="dialog" aria-modal="true" aria-label="Delete my account" data-screen-label="Delete my account" style={sx(panel)}>
        <div style={sx('display:flex;align-items:flex-start;gap:12px;')}>
          <div style={sx('flex:1 1 auto;min-width:0;')}>
            <div style={sx('font-size:.78em;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:600;')}>{owner ? 'Owner account' : isReq ? 'Request' : 'Account'}</div>
            <div style={sx('font-size:1.5em;font-weight:700;line-height:1.2;margin-top:4px;text-wrap:pretty;')}>{sent ? (isReq ? 'Request sent' : 'Your account is deleted') : d?.title ?? 'Delete my account'}</div>
          </div>
          <a href="#" onClick={(e) => { e.preventDefault(); close(); }} style={sx(closeStyle)}>Close</a>
        </div>
        <div style={sx('color:#3C454F;line-height:1.55;margin-top:8px;max-width:62ch;text-wrap:pretty;')}>{plan.error ? plan.error.message : d?.lead ?? p?.reason ?? (plan.loading ? 'Loading…' : '')}</div>
        {d && d.groups.length > 0 && !sent && (
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,230px),1fr));gap:4px 26px;margin-top:14px;')}>
            {d.groups.map((g) => (
              <div key={g.head} style={sx('min-width:0;border-top:1px solid rgba(34,39,46,.14);padding-top:9px;')}>
                <div style={sx('display:flex;align-items:center;gap:8px;margin-bottom:4px;')}>
                  <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + (g.tone === 'deleted' ? '#B3261E' : '#54606C') + ';')}></span>
                  <span style={sx('font-size:.78em;letter-spacing:.12em;text-transform:uppercase;font-weight:700;color:#22272E;line-height:1.3;white-space:nowrap;')}>{g.head}</span>
                </div>
                {g.items.map((it) => (
                  <div key={it.t} style={sx('line-height:1.45;color:#22272E;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.06);text-wrap:pretty;')}>
                    {it.t}
                    {it.why && <span style={sx('display:block;font-size:.86em;color:#54606C;margin-top:1px;')}>{it.why}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {d?.rule && !sent && (
          <div style={sx('display:flex;gap:9px;align-items:flex-start;margin-top:12px;padding:9px 12px;border-radius:9px;background:rgba(79,138,214,.09);color:#22272E;line-height:1.5;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;margin-top:.5em;')}></span>
            <span style={sx('min-width:0;text-wrap:pretty;')}>{d.rule}</span>
          </div>
        )}
        {canAct && (
          <div style={sx('margin-top:16px;border-top:1px solid rgba(34,39,46,.14);padding-top:12px;')}>
            <label htmlFor="del-confirm" style={sx('display:block;font-weight:600;color:#22272E;')}>{isReq ? 'Type DELETE to send the request' : 'Type DELETE to confirm. This can’t be undone.'}</label>
            <div style={sx('display:flex;gap:10px;flex-wrap:wrap;row-gap:8px;margin-top:8px;align-items:center;')}>
              <input id="del-confirm" type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" style={sx(inputStyle)} />
              <a href="#" role="button" aria-disabled={!ok || busy || !!roReason || undefined} title={roReason ?? undefined} onClick={go} style={sx(goStyle)}>
                {isReq ? 'Send request' : 'Delete my account'}
              </a>
            </div>
            {error && <div role="alert" style={sx('color:#B3261E;margin-top:8px;line-height:1.45;')}>{error}</div>}
          </div>
        )}
        {sent && (
          <div style={sx('display:flex;gap:10px;align-items:flex-start;margin-top:16px;padding:12px 14px;border-radius:10px;background:rgba(31,107,82,.09);color:#22272E;line-height:1.5;')}>
            <span style={sx('width:8px;height:8px;border-radius:50%;background:#1F6B52;flex:0 0 8px;margin-top:.5em;')}></span>
            <span style={sx('min-width:0;text-wrap:pretty;')}>
              <b>{isReq ? 'Sent.' : 'Done.'}</b> {doneBody}
            </span>
          </div>
        )}
        <div style={sx('font-size:.86em;color:#54606C;line-height:1.5;margin-top:14px;text-wrap:pretty;')}>{'No app? Use ' + (p?.publicPage ?? PUBLIC_PAGE) + ', or email ' + (p?.supportEmail ?? SUPPORT) + '.'}</div>
      </div>
    </>
  );
}
